import { useState, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import DataResults from './DataResults'
import LLMConfigPanel from './LLMConfigPanel'
import SchemaEditor from './SchemaEditor'
import GenerateSchemaConfirmModal from './GenerateSchemaConfirmModal'
import CreateJobModal from './CreateJobModal'
import RegionalSearchForm from './RegionalSearchForm'
import { dataApi } from '../services/apiService'
import templateService from '../services/templateService'
import locationService from '../services/locationService'
import { RevoGrid } from '@revolist/react-datagrid'
import '@revolist/revogrid-pro/dist/revogrid-pro.css'
import { ExportExcelPlugin } from '@revolist/revogrid-pro'
import * as XLSX from 'xlsx'

// Helper function to format field names
const formatFieldName = (fieldName) => {
  return String(fieldName).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const DataExtraction = () => {
  const { t } = useTranslation()
  const [mode, setMode] = useState('run_job')
  const [searchMode, setSearchMode] = useState('simple') // 'simple', 'excel', or 'regional'
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [formData, setFormData] = useState({
    url: '',
    urls: '',
    search_query: '',
    use_js_rendering: true,
    max_results: 10,
    imprint_deep_research: false,
  })
  
  const [llmConfig, setLlmConfig] = useState({
    enabled: true,
    provider: 'openai/gpt-4o-mini',
    api_key: '',
    instruction: `Extract all contact and address information for this Data (youth welfare office). 
Focus on official contact details including address, phone numbers, email, opening hours, and service areas.
The Data is a German youth welfare office that provides services for children and families.
Extract information in German as it appears on the page.`,
    temperature: 0.1,
    max_tokens: 2000,
    input_format: 'html',
    cache: false,
    chunk_token_threshold: 8000,
    apply_chunking: true,
    overlap_rate: 0.1,
  })
  
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(true)  // Erweiterte Optionen standardmäßig ausgeklappt
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  
  // Excel Batch Processing State
  const [excelFile, setExcelFile] = useState(null)
  const [excelData, setExcelData] = useState([])
  const [excelColumns, setExcelColumns] = useState([])
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [columnMapping, setColumnMapping] = useState({
    searchColumns: [],  // Array of {name, example, isUrl}
    extractColumns: []  // Array of column names
  })
  const [crawlUrls, setCrawlUrls] = useState(false)  // Option to crawl URLs in search columns
  const [crawlAllUrls, setCrawlAllUrls] = useState(false)  // Option to crawl all 15 URLs or stop at first success
  const excelGridRef = useRef(null)
  
  // Regional Search State
  const [regionalConfig, setRegionalConfig] = useState({
    enabled: false,
    state_code: '',
    cities: [],
    search_all_cities: false
  })
  
  const [prequalificationConfig, setPrequalificationConfig] = useState({
    enabled: false,
    inclusion_criteria: '',
    exclusion_criteria: '',
    llm_provider: 'openai/gpt-4o-mini',
    temperature: 0.1
  })
  
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await templateService.getTemplates()
        setTemplates(data.templates || [])
      } catch (error) {
        console.error('Failed to load templates:', error)
      }
    }
    loadTemplates()
  }, [])
  
  const loadTemplate = async (templateId) => {
    if (!templateId) return
    
    try {
      await templateService.useTemplate(templateId)
      const template = templates.find(t => t.id === templateId)
      if (template) {
        const schemaProps = template.schema?.properties || {}
        const schemaWithRequired = {}
        Object.keys(schemaProps).forEach(key => {
          schemaWithRequired[key] = {
            ...schemaProps[key],
            required: template.schema?.required?.includes(key) || false
          }
        })
        setExtractionSchema(schemaWithRequired)
        
        setLlmConfig({
          enabled: true,
          provider: template.llm_provider,
          api_key: '',
          instruction: template.llm_instruction,
          cache: template.llm_cache
        })
        
        // Load prequalification config from template
        if (template.prequalification_config) {
          setPrequalificationConfig({
            enabled: template.prequalification_config.enabled || false,
            inclusion_criteria: template.prequalification_config.inclusion_criteria || '',
            exclusion_criteria: template.prequalification_config.exclusion_criteria || '',
            llm_provider: template.prequalification_config.llm_provider || 'openai/gpt-4o-mini',
            temperature: template.prequalification_config.temperature || 0.1
          })
        }
        
        // Load regional config from template
        if (template.regional_config && template.regional_config.enabled) {
          setRegionalConfig({
            state_code: template.regional_config.state_code || '',
            cities: template.regional_config.cities || [],
            search_all_cities: false,
            include_zip: template.regional_config.include_zip !== false,
            include_city: template.regional_config.include_city !== false
          })
          // Automatically switch to Regional Search mode if template has regional config
          setSearchMode('regional')
        }
        
        setFormData({
          ...formData,
          search_query: template.default_search_query || '',
          max_results: template.max_results || 10
        })
        
        setSelectedTemplate(template)
        toast.success(t('dataExtractor.toast.templateLoaded', { name: template.name }))
      }
    } catch (error) {
      toast.error(t('dataExtractor.toast.templateLoadFailed'))
    }
  }
  
  const [extractionSchema, setExtractionSchema] = useState({
    youth_welfare_office: { type: 'string', description: 'Name of the youth welfare office (Data). Extract the full official name.', required: true },
    city: { type: 'string', description: 'City where the office is located. Extract city name only.', required: false },
    district: { type: 'string', description: 'District or administrative area (Bezirk, Kreis)', required: false },
    postal_code: { type: 'string', description: 'Postal code (PLZ) of the office', required: false },
    street: { type: 'string', description: 'Street name and house number of the office address', required: false },
    contact_person: { type: 'string', description: 'Name of the contact person if mentioned', required: false },
    telephone: { type: 'string', description: 'Main telephone number for contact', required: false },
    email: { type: 'string', description: 'Email address for contact', required: false },
    fax: { type: 'string', description: 'Fax number if available', required: false },
    website: { type: 'string', description: 'Official website URL', required: false },
    opening_hours: { type: 'string', description: 'Office opening hours and consultation times', required: false },
    social_media: { type: 'string', description: 'Social media links or WhatsApp contact if available', required: false },
    postal_code_areas_served: { type: 'string', description: 'Postal code areas or districts served by this office', required: false }
  })

  const [showGenerateSchemaModal, setShowGenerateSchemaModal] = useState(false)
  const [generatedSchemaPreview, setGeneratedSchemaPreview] = useState(null)
  const [isGeneratingSchema, setIsGeneratingSchema] = useState(false)

  const fieldsToSchema = (fields) => {
    const s = {}
    ;(fields || []).forEach((f) => {
      s[f.field_name] = {
        type: f.type || 'string',
        description: f.description || '',
        display_title: f.display_title || (f.field_name && f.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())) || '',
        required: !!f.required
      }
    })
    return s
  }

  const handleGenerateSchemaFromInstruction = async (instruction) => {
    const text = (instruction || llmConfig.instruction || '').trim()
    if (!text) {
      toast.error(t('schemaEditor.generateAiNeedInstruction'))
      return
    }
    setIsGeneratingSchema(true)
    try {
      const data = await templateService.generateSchema(text)
      setGeneratedSchemaPreview(data)
      setShowGenerateSchemaModal(true)
    } catch (err) {
      const msg = err.response?.data?.detail || err.message
      toast.error(msg || 'Schema generation failed')
    } finally {
      setIsGeneratingSchema(false)
    }
  }

  const handleConfirmGeneratedSchema = (replaceOrMerge) => {
    if (!generatedSchemaPreview?.fields?.length) return
    const newSchema = fieldsToSchema(generatedSchemaPreview.fields)
    if (replaceOrMerge === 'replace') {
      setExtractionSchema(newSchema)
      toast.success(t('schemaEditor.generateModal.replace') + ' – ' + generatedSchemaPreview.fields.length + ' ' + t('schemaEditor.fields'))
    } else {
      setExtractionSchema((prev) => ({ ...prev, ...newSchema }))
      toast.success(t('schemaEditor.generateModal.merge') + ' – ' + generatedSchemaPreview.fields.length + ' ' + t('schemaEditor.fields'))
    }
    setShowGenerateSchemaModal(false)
    setGeneratedSchemaPreview(null)
  }
  
  const schemaToBackendFormat = (schema) => {
    const properties = {}
    const required = []
    
    Object.entries(schema).forEach(([fieldName, fieldData]) => {
      properties[fieldName] = {
        type: fieldData.type,
        description: fieldData.description
      }
      if (fieldData.required) {
        required.push(fieldName)
      }
    })
    
    return {
      type: 'object',
      properties,
      required
    }
  }
  
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [streamingData, setStreamingData] = useState({
    isStreaming: false,
    searchResults: null,
    progress: [],
    currentStatus: '',
    totalUrls: 0,
    processedUrls: 0,
    successfulExtractions: 0,
    failedExtractions: 0
  })
  const [singleStreamingData, setSingleStreamingData] = useState({
    isStreaming: false,
    currentStatus: '',
    extractedData: null,
    processingTime: 0
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setResult(null)

    try {
      if (mode === 'single') {
        const payload = {
          url: formData.url,
          use_js_rendering: formData.use_js_rendering,
          imprint_deep_research: formData.imprint_deep_research,
          llm_config: llmConfig.enabled ? {
            provider: llmConfig.provider,
            api_key: llmConfig.api_key || undefined,
            instruction: llmConfig.instruction,
            cache: llmConfig.cache,
            schema: schemaToBackendFormat(extractionSchema)
          } : null
        }

        setSingleStreamingData({
          isStreaming: true,
          currentStatus: 'Starting extraction...',
          extractedData: null,
          processingTime: 0
        })

        try {
          const response = await dataApi.extractSimple(payload)
          
          setSingleStreamingData(prev => ({
            ...prev,
            currentStatus: 'Extraction completed',
            extractedData: response.data,
            processingTime: response.processing_time
          }))
          
          setResult({
            success: response.success,
            mode: 'single',
            data: response.data,
            processing_time: response.processing_time,
            error: response.error
          })
          
          if (response.success) {
            toast.success(t('dataExtractor.toast.extractionSuccess'))
          } else {
            toast.error(response.error || t('dataExtractor.toast.extractionFailed'))
          }
          
        } catch (error) {
          console.error('Single extraction error:', error)
          toast.error(t('dataExtractor.toast.extractionFailed'))
        } finally {
          setSingleStreamingData(prev => ({ ...prev, isStreaming: false }))
        }
      } else if (mode === 'batch') {
        const urlList = formData.urls
          .split('\n')
          .map(url => url.trim())
          .filter(url => url.length > 0)

        if (urlList.length === 0) {
          toast.error(t('dataExtractor.toast.provideAtLeastOneUrl'))
          setLoading(false)
          return
        }

        const payload = {
          urls: urlList,
          use_js_rendering: formData.use_js_rendering,
          imprint_deep_research: formData.imprint_deep_research,
          llm_config: llmConfig.enabled ? {
            provider: llmConfig.provider,
            api_key: llmConfig.api_key || undefined,
            instruction: llmConfig.instruction,
            cache: llmConfig.cache,
            schema: schemaToBackendFormat(extractionSchema)
          } : null
        }

        const response = await dataApi.extractBatch(payload)
        setResult({ ...response, mode: 'batch' })
        
        if (response.success) {
          toast.success(t('dataExtractor.toast.batchSuccess', { count: response.total_processed }))
        } else {
          toast.error(response.error || t('dataExtractor.toast.batchFailed'))
        }
      } else if (mode === 'auto' || mode === 'run_job') {
        // Check if Regional Search Mode
        if (searchMode === 'regional') {
          if (!formData.search_query || formData.search_query.trim().length === 0) {
            toast.error('Bitte geben Sie einen Suchbegriff ein')
            setLoading(false)
            return
          }
          
          if (!regionalConfig.state_code) {
            toast.error('Bitte wählen Sie ein Bundesland aus')
            setLoading(false)
            return
          }
          
          if (regionalConfig.cities.length === 0) {
            toast.error('Bitte wählen Sie mindestens eine Stadt aus')
            setLoading(false)
            return
          }

          // Trigger Regional Search
          await handleRegionalSearch()
          setLoading(false)
          return
        }
        
        // Check if Excel Batch Mode
        if (searchMode === 'excel') {
          if (!excelData || excelData.length === 0) {
            toast.error(t('dataExtractor.toast.uploadExcelFirst'))
            setLoading(false)
            return
          }

          // Trigger Excel Batch Processing
          await handleExcelBatchProcessing(excelData, columnMapping)
          setLoading(false)
          return
        }

        // Simple Search Mode validation
        if (!formData.search_query || formData.search_query.trim().length === 0) {
          toast.error(t('dataExtractor.toast.enterSearchQuery'))
          setLoading(false)
          return
        }

        if (mode === 'run_job' && !selectedTemplate) {
          toast.error(t('dataExtractor.toast.selectTemplate'))
          setLoading(false)
          return
        }

        await handleStreamingAutoSearch()
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract data')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setExcelFile(file)
    
    // Open modal and trigger Excel import via RevoGrid Pro plugin
    setShowExcelModal(true)
    
    toast.success(t('dataExtractor.toast.fileReadyImport', { name: file.name }))
  }

  const handleRegionalSearch = async () => {
    toast.success('🌍 Starte regionale Suche mit Live-Updates...')
    
    try {
      const backendSchema = schemaToBackendFormat(extractionSchema)
      
      const payload = {
        search_query: formData.search_query,
        max_results: formData.max_results,
        imprint_deep_research: formData.imprint_deep_research,
        regional_search: {
          enabled: true,
          state_code: regionalConfig.state_code,
          cities: regionalConfig.cities,
          search_all_cities: regionalConfig.search_all_cities,
          include_zip: regionalConfig.include_zip !== false,
          include_city: regionalConfig.include_city !== false
        },
        prequalification: prequalificationConfig,
        extraction_schema: backendSchema,
        llm_config: {
          provider: llmConfig.provider,
          api_key: llmConfig.api_key || undefined,
          instruction: llmConfig.instruction,
          schema: backendSchema,
          cache: llmConfig.cache
        }
      }

      console.log('🌍 Regional search payload:', payload)

      // Initialize streaming data
      setStreamingData({
        isStreaming: true,
        searchResults: null,
        progress: [],
        currentStatus: '🌍 Starte regionale Suche...',
        totalUrls: 0,
        processedUrls: 0,
        successfulExtractions: 0,
        failedExtractions: 0,
        sessionId: null
      })

      // Start streaming with updated handler
      await dataApi.regionalSearchStream(payload, handleRegionalStreamingUpdate)

    } catch (error) {
      console.error('Regional search failed:', error)
      toast.error('Regionale Suche fehlgeschlagen: ' + (error.message || 'Unbekannter Fehler'))
      setStreamingData(prev => ({ ...prev, isStreaming: false }))
    }
  }

  const handleRegionalStreamingUpdate = (update) => {
    console.log('🔄 Regional Stream Update:', update)
    
    switch (update.type) {
      case 'regional_search_start':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          sessionId: update.data?.session_id,
          totalUrls: update.data?.max_results_per_city * update.data?.total_cities || 0
        }))
        toast.info(update.message)
        break

      case 'search_progress':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message
        }))
        break

      case 'search_complete':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          totalUrls: update.data?.qualified_urls || 0,
          searchResults: update.data
        }))
        toast.success(update.message)
        break

      case 'extraction_start':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          totalUrls: update.data?.total_urls || prev.totalUrls
        }))
        break

      case 'extraction_progress':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          processedUrls: update.data?.progress || prev.processedUrls,
          successfulExtractions: update.data?.successful || prev.successfulExtractions,
          failedExtractions: update.data?.failed || prev.failedExtractions
        }))
        break

      case 'extraction_success':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          processedUrls: prev.processedUrls + 1,
          successfulExtractions: prev.successfulExtractions + 1,
          progress: [...prev.progress, {
            url: update.data?.url,
            success: true,
            data: update.data?.extracted_data
          }]
        }))
        break

      case 'extraction_failed':
      case 'extraction_error':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          processedUrls: prev.processedUrls + 1,
          failedExtractions: prev.failedExtractions + 1,
          progress: [...prev.progress, {
            url: update.data?.url,
            success: false,
            error: update.data?.error
          }]
        }))
        break

      case 'regional_complete':
        setStreamingData(prev => ({
          ...prev,
          isStreaming: false,
          currentStatus: update.message
        }))
        toast.success(update.message, { duration: 5000 })
        
        // Set final result
        setResult({
          success: true,  // ✅ FIX: Mark as successful
          mode: 'regional',
          session_id: update.data?.session_id,
          results: update.data?.results || [],
          total_extracted: update.data?.total_processed || 0,
          successful: update.data?.successful_extractions || 0,
          failed: update.data?.failed_extractions || 0,
          processing_time: update.data?.processing_time
        })
        break

      case 'warning':
        toast.warning(update.message)
        setStreamingData(prev => ({
          ...prev,
          isStreaming: false,
          currentStatus: update.message
        }))
        break

      case 'error':
        toast.error(update.message)
        setStreamingData(prev => ({
          ...prev,
          isStreaming: false,
          currentStatus: `❌ ${update.message}`
        }))
        break

      default:
        console.log('Unknown update type:', update.type)
    }
  }

  const handleExcelBatchProcessing = async (data, mapping) => {
    try {
      setLoading(true)
      toast.success('Starting batch processing...')
      
      // Extract column names from search columns objects
      const searchColumnNames = mapping.searchColumns.map(col => col.name)
      
      // Prepare rows for batch processing
      const rows = data.map((row, index) => ({
        row_index: index,
        search_data: Object.fromEntries(
          searchColumnNames.map(col => [col, row[col] || ''])
        ),
        extract_fields: mapping.extractColumns.filter(c => c && c !== '')
      }))
      
      // Call batch processing API
      const response = await dataApi.excelBatchProcessing({
        rows,
        search_columns: searchColumnNames,
        extract_columns: mapping.extractColumns.filter(c => c && c !== ''),
        max_results_per_search: 15,
        use_js_rendering: formData.use_js_rendering,
        crawl_urls: crawlUrls,
        crawl_all_urls: crawlAllUrls,
        imprint_deep_research: formData.imprint_deep_research,
        llm_config: {
          ...llmConfig,
          schema: extractionSchema
        }
      })
      
      if (response.success) {
        // Export results back to Excel
        exportToExcel(response.results, mapping)
        
        toast.success(`Batch processing completed! ${response.successful_rows}/${response.total_rows} rows processed successfully`)
        
        // Show results in the UI
        setResult({
          success: true,
          mode: 'excel_batch',
          data: response.results,
          processing_time: response.processing_time,
          total_rows: response.total_rows,
          successful_rows: response.successful_rows,
          failed_rows: response.failed_rows
        })
      } else {
        toast.error('Batch processing failed: ' + response.error)
      }
      
    } catch (error) {
      console.error('Batch processing failed:', error)
      toast.error('Batch processing failed: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const exportToExcel = (results, mapping) => {
    // Create Excel file with results
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(results)
    
    // Auto-size columns
    const colWidths = Object.keys(results[0] || {}).map(key => ({
      wch: Math.max(key.length, 20)
    }))
    ws['!cols'] = colWidths
    
    XLSX.utils.book_append_sheet(wb, ws, 'Results')
    
    const filename = `data_batch_results_${Date.now()}.xlsx`
    XLSX.writeFile(wb, filename, { 
      bookType: 'xlsx',
      compression: true
    })
    
    toast.success('Results exported to Excel!')
  }

  const handleStreamingAutoSearch = async () => {
    const backendSchema = schemaToBackendFormat(extractionSchema)
    
    const payload = {
      search_query: formData.search_query,
      max_results: formData.max_results,
      use_js_rendering: formData.use_js_rendering,
      update_interval: 3,
      imprint_deep_research: formData.imprint_deep_research,
      llm_config: llmConfig.enabled ? {
        provider: llmConfig.provider,
        api_key: llmConfig.api_key || undefined,
        instruction: llmConfig.instruction,
        cache: llmConfig.cache,
        schema: backendSchema
      } : null
    }
    
    setStreamingData({
      isStreaming: true,
      searchResults: null,
      progress: [],
      currentStatus: 'Starting search...',
      totalUrls: 0,
      processedUrls: 0,
      successfulExtractions: 0,
      failedExtractions: 0
    })

    try {
      await dataApi.autoSearchStream(payload, handleStreamingUpdate)
    } catch (error) {
      console.error('Streaming error:', error)
      toast.error('Failed to start streaming search')
      setStreamingData(prev => ({ ...prev, isStreaming: false }))
    }
  }

  const handleSingleStreamingUpdate = (update) => {
    switch (update.type) {
      case 'extraction_start':
        setSingleStreamingData(prev => ({
          ...prev,
          currentStatus: update.message
        }))
        break

      case 'crawling_start':
        setSingleStreamingData(prev => ({
          ...prev,
          currentStatus: update.message
        }))
        break

      case 'extraction_success':
        setSingleStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          extractedData: update.data,
          processingTime: update.processing_time
        }))
        toast.success('Data extracted successfully!')
        break

      case 'extraction_error':
        setSingleStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          processingTime: update.processing_time
        }))
        toast.error(update.message)
        break

      case 'extraction_complete':
        setSingleStreamingData(prev => ({
          ...prev,
          isStreaming: false,
          currentStatus: update.message
        }))
        
        if (update.success) {
          setResult({
            success: true,
            mode: 'single',
            data: singleStreamingData.extractedData,
            processing_time: update.processing_time
          })
        } else {
          setResult({
            success: false,
            mode: 'single',
            error: 'Extraction failed',
            processing_time: update.processing_time
          })
        }
        break
    }
  }

  const handleStreamingUpdate = (update) => {
    switch (update.type) {
      case 'search_start':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message
        }))
        break

      case 'search_complete':
        setStreamingData(prev => ({
          ...prev,
          searchResults: update.data,
          currentStatus: update.message,
          totalUrls: update.data.urls.length
        }))
        toast.success(`Found ${update.data.urls.length} URLs using ${update.data.search_source}`)
        break

      case 'crawl_start':
        setStreamingData(prev => ({
          ...prev,
          currentStatus: update.message,
          totalUrls: update.data.total_urls
        }))
        break

      case 'crawl_progress':
        setStreamingData(prev => {
          let newProcessedUrls = prev.processedUrls;
          if (update.data.progress !== undefined) {
            newProcessedUrls = update.data.progress;
          } else if (update.data.success !== undefined) {
            newProcessedUrls = prev.processedUrls + 1;
          }
          
          const progressEntry = {
            url: update.data.current_url || update.data.url || '',
            success: update.data.success,
            extracted_data: update.data.extracted_data || null,
            error: update.data.error || null,
            timestamp: update.timestamp || new Date().toISOString()
          };
          
          return {
            ...prev,
            currentStatus: update.message,
            processedUrls: newProcessedUrls,
            progress: [...prev.progress, progressEntry],
            successfulExtractions: update.data.success === true ? prev.successfulExtractions + 1 : prev.successfulExtractions,
            failedExtractions: update.data.success === false ? prev.failedExtractions + 1 : prev.failedExtractions
          };
        })
        break

      case 'crawl_complete':
        setStreamingData(prev => {
          const finalResults = update.data?.results || prev.progress
            .filter(p => p.success && p.extracted_data)
            .map(p => ({
              success: true,
              data: p.extracted_data,
              processing_time: null
            }));
          
          setResult({
            success: true,
            mode: 'auto',
            search_query: formData.search_query,
            urls_found: prev.searchResults?.urls || [],
            total_urls_found: prev.totalUrls,
            search_source: prev.searchResults?.search_source,
            total_processed: update.data?.total_processed || prev.processedUrls,
            successful_extractions: update.data?.successful_extractions || prev.successfulExtractions,
            failed_extractions: update.data?.failed_extractions || prev.failedExtractions,
            results: finalResults,
            processing_time: update.data?.processing_time
          })
          
          toast.success(`Extraction complete! ${update.data?.successful_extractions || prev.successfulExtractions} successful, ${update.data?.failed_extractions || prev.failedExtractions} failed`)
          
          return {
            ...prev,
            isStreaming: false,
            currentStatus: update.message
          };
        })
        break

      case 'error':
        setStreamingData(prev => ({
          ...prev,
          isStreaming: false,
          currentStatus: update.message
        }))
        toast.error(update.message)
        break
    }
  }

  return (
    <div className="space-y-6">
      {/* Mode-specific Banner */}
      {(mode === 'auto' || mode === 'run_job') && (
        <section className="gradient-primary rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="fa-solid fa-rocket text-2xl"></i>
          </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-xl font-bold mb-2">
                {mode === 'run_job' ? t('dataExtractor.banner.runJobTitle') : t('dataExtractor.banner.autoTitle')}
              </h3>
              <p className="text-white/90 mb-3">
                {mode === 'run_job' 
                  ? t('dataExtractor.banner.runJobDesc')
                  : t('dataExtractor.banner.autoDesc')
                }
              </p>
              <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                <p className="text-sm font-semibold mb-2">{t('dataExtractor.banner.tipTitle')}</p>
                <p className="text-sm text-white/80">{t('dataExtractor.banner.tipDesc')}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Page Header */}
      <section className="bg-white border-b border-light-200/20 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-dark-300 mb-2">{t('dataExtractor.title')}</h2>
            <p className="text-dark-100">{t('dataExtractor.subtitle')}</p>
          </div>
          <div className="flex space-x-3">
            <button 
              onClick={handleSubmit}
              disabled={loading || streamingData.isStreaming || singleStreamingData.isStreaming}
              className="px-6 py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-semibold shadow-lg transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fa-solid fa-play mr-2"></i>
              {loading || streamingData.isStreaming || singleStreamingData.isStreaming ? t('dataExtractor.processing') : t('dataExtractor.runJob')}
            </button>
            <button className="px-6 py-3 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 rounded-lg font-semibold text-dark-300 transition-all flex items-center">
              <i className="fa-solid fa-save mr-2"></i>
              {t('dataExtractor.saveTemplate')}
            </button>
            <button 
              onClick={() => setShowScheduleModal(true)}
              className="px-6 py-3 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 rounded-lg font-semibold text-dark-300 transition-all flex items-center"
            >
              <i className="fa-solid fa-clock mr-2"></i>
              {t('dataExtractor.scheduleJob')}
            </button>
        </div>
        </div>
      </section>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mode Selection */}
        <section className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
          <h3 className="text-lg font-bold text-dark-300 mb-4 flex items-center">
            <i className="fa-solid fa-sliders text-primary-500 mr-2"></i>
            {t('dataExtractor.mode.title')}
          </h3>
          
          <div className="grid grid-cols-4 gap-4">
            <button
              type="button"
              onClick={() => setMode('run_job')}
              className={`p-6 border-2 rounded-xl hover:shadow-md transition-all ${
                mode === 'run_job'
                  ? 'border-primary-500 bg-primary-500/5'
                  : 'border-light-200/30 hover:border-light-200/50'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <i className={`fa-solid fa-play text-3xl ${mode === 'run_job' ? 'text-primary-500' : 'text-light-200'}`}></i>
                {mode === 'run_job' && (
                  <span className="px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full">{t('dataExtractor.mode.active')}</span>
                )}
              </div>
              <h4 className="font-bold text-dark-300 mb-2">{t('dataExtractor.mode.runJob')}</h4>
              <p className="text-sm text-dark-100">{t('dataExtractor.mode.runJobDesc')}</p>
            </button>

            <button
              type="button"
              onClick={() => setMode('auto')}
              className={`p-6 border-2 rounded-xl hover:shadow-md transition-all ${
                mode === 'auto'
                  ? 'border-primary-500 bg-primary-500/5'
                  : 'border-light-200/30 hover:border-light-200/50'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <i className={`fa-solid fa-wand-magic-sparkles text-3xl ${mode === 'auto' ? 'text-primary-500' : 'text-light-200'}`}></i>
                {mode === 'auto' && (
                  <span className="px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full">{t('dataExtractor.mode.active')}</span>
                )}
              </div>
              <h4 className="font-bold text-dark-300 mb-2">{t('dataExtractor.mode.auto')}</h4>
              <p className="text-sm text-dark-100">{t('dataExtractor.mode.autoDesc')}</p>
            </button>
            
            <button
              type="button"
              onClick={() => setMode('single')}
              className={`p-6 border-2 rounded-xl hover:shadow-md transition-all ${
                mode === 'single'
                  ? 'border-primary-500 bg-primary-500/5'
                  : 'border-light-200/30 hover:border-light-200/50'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <i className={`fa-solid fa-link text-3xl ${mode === 'single' ? 'text-primary-500' : 'text-light-200'}`}></i>
                {mode === 'single' && (
                  <span className="px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full">{t('dataExtractor.mode.active')}</span>
                )}
              </div>
              <h4 className="font-bold text-dark-300 mb-2">{t('dataExtractor.mode.single')}</h4>
              <p className="text-sm text-dark-100">{t('dataExtractor.mode.singleDesc')}</p>
            </button>
            
            <button
              type="button"
              onClick={() => setMode('batch')}
              className={`p-6 border-2 rounded-xl hover:shadow-md transition-all ${
                mode === 'batch'
                  ? 'border-primary-500 bg-primary-500/5'
                  : 'border-light-200/30 hover:border-light-200/50'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <i className={`fa-solid fa-list text-3xl ${mode === 'batch' ? 'text-primary-500' : 'text-light-200'}`}></i>
                {mode === 'batch' && (
                  <span className="px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full">{t('dataExtractor.mode.active')}</span>
                )}
              </div>
              <h4 className="font-bold text-dark-300 mb-2">{t('dataExtractor.mode.batch')}</h4>
              <p className="text-sm text-dark-100">{t('dataExtractor.mode.batchDesc')}</p>
            </button>
          </div>
        </section>

        {/* Template Selector (Run Job mode) */}
        {mode === 'run_job' && (
          <section className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
            <div className="mb-6">
              <label className="block mb-2">
                <span className="text-sm font-bold text-dark-300">{t('dataExtractor.template.select')} <span className="text-primary-500">*</span></span>
                </label>
              <div className="relative">
                <select
                  onChange={(e) => loadTemplate(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 appearance-none bg-white"
                  defaultValue=""
                >
                  <option value="">{t('dataExtractor.template.choose')}</option>
                  {templates.map(tmpl => (
                    <option key={tmpl.id} value={tmpl.id}>
                      {tmpl.name} ({Object.keys(tmpl.schema?.properties || {}).length} {t('schemaEditor.fields')})
                    </option>
                  ))}
                </select>
                <i className="fa-solid fa-chevron-down absolute right-4 top-4 text-light-200 pointer-events-none"></i>
              </div>
                {selectedTemplate && (
                <p className="text-xs text-dark-100 mt-2">
                    ✅ {t('dataExtractor.template.loaded')}: {selectedTemplate.name} • {Object.keys(selectedTemplate.schema?.properties || {}).length} {t('schemaEditor.fields')} • {selectedTemplate.llm_provider}
                  </p>
                )}
              </div>
          </section>
        )}

              {/* Search Mode Switcher & Input */}
        {(mode === 'auto' || mode === 'run_job') && (
          <section className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
            {/* Mode Switcher */}
            <div className="mb-6">
              <div className="flex bg-light-100 rounded-lg p-1 border border-light-200/30 w-full">
                <button
                  onClick={() => setSearchMode('simple')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                    searchMode === 'simple' 
                      ? 'bg-white text-primary-600 shadow-md' 
                      : 'text-dark-100 hover:text-dark-300'
                  }`}
                >
                  <i className="fa-solid fa-magnifying-glass mr-2"></i>
                  {t('dataExtractor.search.simpleSearch')}
                </button>
                <button
                  onClick={() => setSearchMode('regional')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                    searchMode === 'regional' 
                      ? 'bg-white text-primary-600 shadow-md' 
                      : 'text-dark-100 hover:text-dark-300'
                  }`}
                >
                  <i className="fa-solid fa-map-location-dot mr-2"></i>
                  Regional Search
                </button>
                <button
                  onClick={() => setSearchMode('excel')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                    searchMode === 'excel' 
                      ? 'bg-white text-primary-600 shadow-md' 
                      : 'text-dark-100 hover:text-dark-300'
                  }`}
                >
                  <i className="fa-solid fa-file-excel mr-2"></i>
                  {t('dataExtractor.search.excelBatch')}
                </button>
              </div>
            </div>

            {/* Simple Search Mode */}
            {searchMode === 'simple' ? (
              <>
                <label className="block mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-dark-300">{t('dataExtractor.search.whatLookingFor')} <span className="text-primary-500">*</span></span>
                    <span className="text-xs text-dark-100">{t('dataExtractor.search.required')}</span>
                  </div>
                    <textarea
                      name="search_query"
                      value={formData.search_query}
                      onChange={handleChange}
                    rows="4"
                    placeholder={selectedTemplate?.search_query_template || t('dataExtractor.search.placeholder')}
                      required
                    className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200"
                  />
                </label>
                
                <div className="bg-primary-500/5 border-l-4 border-primary-500 rounded-r-lg p-4 mt-4">
                  <p className="text-sm font-semibold text-dark-300 mb-2">{t('dataExtractor.search.keepSimple')}</p>
                  <ul className="space-y-1 text-sm text-dark-100">
                    <li className="flex items-center">
                      <i className="fa-solid fa-check-circle text-primary-500 mr-2"></i>
                      {t('dataExtractor.search.example1')}
                    </li>
                    <li className="flex items-center">
                      <i className="fa-solid fa-check-circle text-primary-500 mr-2"></i>
                      {t('dataExtractor.search.example2')}
                    </li>
                    <li className="flex items-center">
                      <i className="fa-solid fa-check-circle text-primary-500 mr-2"></i>
                      {t('dataExtractor.search.example3')}
                    </li>
                  </ul>
                </div>
              </>
            ) : searchMode === 'regional' ? (
              /* Regional Search Mode */
              <div>
                <label className="block mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-dark-300">Was suchen Sie? <span className="text-primary-500">*</span></span>
                    <span className="text-xs text-dark-100">Pflichtfeld</span>
                  </div>
                  <textarea
                    name="search_query"
                    value={formData.search_query}
                    onChange={handleChange}
                    rows="2"
                    placeholder="z.B. 'Tafel Essensausgabe' oder 'Notunterkunft'"
                    required
                    className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200"
                  />
                </label>

                <RegionalSearchForm
                  searchQuery={formData.search_query}
                  regionalConfig={regionalConfig}
                  prequalificationConfig={prequalificationConfig}
                  onRegionalConfigChange={setRegionalConfig}
                  onPrequalificationChange={setPrequalificationConfig}
                />
              </div>
            ) : (
              /* Excel Batch Processing Mode */
              <div>
                <label className="block mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-dark-300">{t('dataExtractor.excel.uploadFile')} <span className="text-primary-500">*</span></span>
                    <span className="text-xs text-dark-100">{t('dataExtractor.excel.requiredFile')}</span>
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelUpload}
                      className="hidden"
                      id="excel-upload"
                    />
                    <label
                      htmlFor="excel-upload"
                      className="flex items-center justify-center w-full px-6 py-8 border-2 border-dashed border-light-200/30 rounded-lg hover:border-primary-500 hover:bg-primary-50/30 transition-all cursor-pointer"
                    >
                      <div className="text-center">
                        <i className="fa-solid fa-file-excel text-5xl text-primary-500 mb-3"></i>
                        <p className="text-sm font-semibold text-dark-300">
                          {excelFile ? excelFile.name : t('dataExtractor.excel.clickUpload')}
                        </p>
                        <p className="text-xs text-dark-100 mt-1">
                          {t('dataExtractor.excel.fileDescription')}
                        </p>
                      </div>
                    </label>
                  </div>
                </label>
                
                {excelData.length === 0 ? (
                  <div className="bg-blue-500/5 border-l-4 border-blue-500 rounded-r-lg p-4 mt-4">
                    <p className="text-sm font-semibold text-dark-300 mb-2">
                      <i className="fa-solid fa-info-circle text-blue-500 mr-2"></i>
                      {t('dataExtractor.excel.format')}
                    </p>
                    <ul className="space-y-1 text-sm text-dark-100">
                      <li className="flex items-start">
                        <i className="fa-solid fa-check-circle text-blue-500 mr-2 mt-0.5"></i>
                        <span>{t('dataExtractor.excel.formatFirstRow')}</span>
                      </li>
                      <li className="flex items-start">
                        <i className="fa-solid fa-check-circle text-blue-500 mr-2 mt-0.5"></i>
                        <span>{t('dataExtractor.excel.formatFilledCols')}</span>
                      </li>
                      <li className="flex items-start">
                        <i className="fa-solid fa-check-circle text-blue-500 mr-2 mt-0.5"></i>
                        <span>{t('dataExtractor.excel.formatEmptyCols')}</span>
                      </li>
                    </ul>
                  </div>
                ) : (
                  <div className="bg-green-500/10 border-l-4 border-green-500 rounded-r-lg p-4 mt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-dark-300 mb-2">
                          <i className="fa-solid fa-check-circle text-green-600 mr-2"></i>
                          {t('dataExtractor.excel.dataLoaded')}
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-xs text-dark-100">
                          <div>
                            <span className="font-semibold">{t('dataExtractor.excel.rows')}:</span> {excelData.length}
                          </div>
                          <div>
                            <span className="font-semibold">{t('dataExtractor.excel.searchFields')}:</span> {columnMapping.searchColumns.length}
                          </div>
                          <div>
                            <span className="font-semibold">{t('dataExtractor.excel.extractFields')}:</span> {columnMapping.extractColumns.filter(c => c).length}
                          </div>
                        </div>
                        {crawlUrls && (
                          <div className="mt-2 text-xs text-blue-700 font-semibold">
                            <i className="fa-solid fa-link mr-1"></i>
                            {t('dataExtractor.excel.urlCrawlingEnabled')}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setExcelFile(null)
                          setExcelData([])
                          setColumnMapping({ searchColumns: [], extractColumns: [] })
                          setCrawlUrls(false)
                        }}
                        className="text-red-600 hover:text-red-800 text-xs font-semibold"
                      >
                        <i className="fa-solid fa-xmark mr-1"></i>
                        {t('dataExtractor.excel.clear')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Single URL Input */}
        {mode === 'single' && (
          <section className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
            <label className="block">
              <span className="text-sm font-bold text-dark-300 mb-2 block">{t('dataExtractor.singleUrl.label')} <span className="text-primary-500">*</span></span>
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder={t('dataExtractor.singleUrl.placeholder')}
                required
                className="input-field"
              />
              <p className="text-xs text-dark-100 mt-2">{t('dataExtractor.singleUrl.description')}</p>
                </label>
          </section>
        )}

        {/* Batch URLs Input */}
        {mode === 'batch' && (
          <section className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
            <label className="block">
              <span className="text-sm font-bold text-dark-300 mb-2 block">{t('dataExtractor.batchUrls.label')} <span className="text-primary-500">*</span></span>
                <textarea
                  name="urls"
                  value={formData.urls}
                  onChange={handleChange}
                  placeholder={t('dataExtractor.batchUrls.placeholder')}
                  required
                  rows="8"
                  className="input-field font-mono text-sm"
                />
              <p className="text-xs text-dark-100 mt-2">
                  {t('dataExtractor.batchUrls.description')}
                </p>
            </label>
          </section>
        )}

        {/* Max Results */}
        {(mode === 'auto' || mode === 'run_job') && (
          <section className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
            <label className="block">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-dark-300">{t('dataExtractor.maxResults.label')}</span>
                <span className="text-xs bg-primary-500/10 text-primary-800 px-2 py-1 rounded-full font-semibold">{t('dataExtractor.maxResults.hint')}</span>
              </div>

              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  name="max_results"
                  min="1"
                  max="10000"
                  value={Math.min(Math.max(1, Number(formData.max_results) || 1), 10000)}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_results: parseInt(e.target.value, 10) }))}
                  className="flex-1 h-2 bg-light-200/30 rounded-lg appearance-none cursor-pointer accent-primary-500"
                />
                <input
                  type="number"
                  name="max_results"
                  min="1"
                  max="100000"
                  value={formData.max_results}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10)
                    setFormData(prev => ({
                      ...prev,
                      max_results: Number.isFinite(n) ? Math.min(100000, Math.max(1, n)) : prev.max_results
                    }))
                  }}
                  className="w-28 px-4 py-2 bg-light-100 border border-light-200/30 rounded-lg text-center font-bold text-dark-300 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                />
            </div>
              
              <p className="text-sm text-dark-100 mt-3">{t('dataExtractor.maxResults.description')}</p>
            </label>
          </section>
        )}

        {/* URL Preview for Regional Search */}
        {(mode === 'auto' || mode === 'run_job') && searchMode === 'regional' && regionalConfig.cities && regionalConfig.cities.length > 0 && (
          <section className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border-2 border-blue-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-blue-900 flex items-center">
                <i className="fa-solid fa-list-check text-blue-600 mr-2"></i>
                Suchanfragen - Vorschau
              </h3>
              <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                {regionalConfig.cities.length} {regionalConfig.cities.length === 1 ? 'Suchanfrage' : 'Suchanfragen'}
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-blue-200 max-h-64 overflow-y-auto">
              <div className="space-y-2">
                {regionalConfig.cities.slice(0, 10).map((city, idx) => {
                  const searchParts = [
                    formData.search_query || '[Suchbegriff]',
                    regionalConfig.include_zip ? city.zip : null,
                    regionalConfig.include_city ? city.city : null
                  ].filter(Boolean)
                  
                  return (
                    <div key={idx} className="flex items-center p-2 bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                      <i className="fa-solid fa-magnifying-glass text-blue-500 mr-3 text-sm"></i>
                      <span className="text-sm text-blue-900 font-medium">
                        {searchParts.join(' ')}
                      </span>
                      <span className="ml-auto text-xs text-blue-600">
                        → max. {formData.max_results} URLs
                      </span>
                    </div>
                  )
                })}
                {regionalConfig.cities.length > 10 && (
                  <div className="text-center py-2 text-sm text-blue-700 font-semibold">
                    <i className="fa-solid fa-ellipsis mr-2"></i>
                    ... und {regionalConfig.cities.length - 10} weitere Suchanfragen
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="text-xs text-blue-600 font-semibold mb-1">Gesamt Suchanfragen</div>
                <div className="text-2xl font-bold text-blue-900">{regionalConfig.cities.length}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="text-xs text-blue-600 font-semibold mb-1">Max. URLs pro Suche</div>
                <div className="text-2xl font-bold text-blue-900">{formData.max_results}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="text-xs text-blue-600 font-semibold mb-1">Total zu durchsuchen</div>
                <div className="text-2xl font-bold text-blue-900">
                  {regionalConfig.cities.length * formData.max_results}
                </div>
              </div>
            </div>
            
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start">
              <i className="fa-solid fa-info-circle text-yellow-600 mr-2 mt-0.5"></i>
              <p className="text-xs text-yellow-800">
                <strong>Hinweis:</strong> Die Crawler API wird diese {regionalConfig.cities.length} Suchanfragen durchführen 
                und bis zu {formData.max_results} URLs pro Suche finden. Der Crawler startet erst beim Klick auf "Job ausführen".
              </p>
            </div>
          </section>
        )}

        {/* LLM Configuration */}
        <LLMConfigPanel config={llmConfig} onChange={setLlmConfig} />

        {/* Schema Editor */}
        <SchemaEditor
          schema={extractionSchema}
          onChange={setExtractionSchema}
          llmInstruction={llmConfig.instruction}
          onGenerateSchema={handleGenerateSchemaFromInstruction}
          isGeneratingSchema={isGeneratingSchema}
        />

        <GenerateSchemaConfirmModal
          open={showGenerateSchemaModal}
          onClose={() => { setShowGenerateSchemaModal(false); setGeneratedSchemaPreview(null) }}
          generatedFields={generatedSchemaPreview?.fields || []}
          onConfirm={handleConfirmGeneratedSchema}
        />

        {/* Advanced Options */}
        <section className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
          <button 
            type="button"
            onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
            className="w-full flex items-center justify-between mb-4"
          >
            <h3 className="text-lg font-bold text-dark-300 flex items-center">
              <i className="fa-solid fa-gears text-dark-100 mr-2"></i>
              {t('dataExtractor.advanced.title')}
            </h3>
            <i className={`fa-solid fa-chevron-${showAdvancedConfig ? 'up' : 'down'} text-light-200`}></i>
          </button>
          
          {showAdvancedConfig && (
            <div className="space-y-4 pt-4 border-t border-light-200/20">
              <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                <div>
                  <div className="text-sm font-semibold text-dark-300">{t('dataExtractor.advanced.jsRendering')}</div>
                  <div className="text-xs text-dark-100">{t('dataExtractor.advanced.jsRenderingDesc')}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="use_js_rendering"
                    checked={formData.use_js_rendering}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-primary-800 peer-checked:to-primary-500"></div>
                </label>
              </div>
              <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                <div>
                  <div className="text-sm font-semibold text-dark-300">{t('dataExtractor.advanced.imprintDeepResearch')}</div>
                  <div className="text-xs text-dark-100">{t('dataExtractor.advanced.imprintDeepResearchDesc')}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="imprint_deep_research"
                    checked={formData.imprint_deep_research}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-primary-800 peer-checked:to-primary-500"></div>
                </label>
              </div>
            </div>
          )}
        </section>
        </form>

      {/* Streaming Progress Displays */}
      {singleStreamingData.isStreaming && (
        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl shadow-sm border border-light-200/20 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="animate-spin rounded-full h-6 w-6">
              <i className="fa-solid fa-spinner text-primary-500"></i>
            </div>
            <h3 className="text-lg font-semibold text-primary-900">{t('dataExtractor.streaming.single')}</h3>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-primary-800 font-medium">{singleStreamingData.currentStatus}</p>
          </div>

          {singleStreamingData.processingTime > 0 && (
            <div className="text-xs text-primary-600">
              {t('dataExtractor.streaming.processingTime')}: {singleStreamingData.processingTime.toFixed(1)}s
            </div>
          )}

          {singleStreamingData.extractedData && (
            <div className="mt-4 p-3 bg-white rounded-lg border">
              <h4 className="font-semibold text-dark-300 mb-2 flex items-center">
                <i className="fa-solid fa-check-circle text-green-600 mr-2"></i>
                {t('dataExtractor.streaming.dataExtracted')}
              </h4>
              <div className="text-sm text-dark-100">
                <p><strong>{t('dataExtractor.streaming.office')}:</strong> {singleStreamingData.extractedData.youth_welfare_office || 'N/A'}</p>
                <p><strong>{t('dataExtractor.streaming.city')}:</strong> {singleStreamingData.extractedData.city || 'N/A'}</p>
                <p><strong>{t('dataExtractor.streaming.contact')}:</strong> {singleStreamingData.extractedData.contact_person || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {streamingData.isStreaming && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl shadow-sm border border-light-200/20 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="animate-spin rounded-full h-6 w-6">
              <i className="fa-solid fa-spinner text-primary-500"></i>
            </div>
            <h3 className="text-lg font-semibold text-primary-900">{t('dataExtractor.streaming.auto')}</h3>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-primary-800 font-medium">{streamingData.currentStatus}</p>
          </div>

          {streamingData.searchResults && (
            <div className="mb-4 p-3 bg-white rounded-lg border">
              <h4 className="font-semibold text-dark-300 mb-2 flex items-center">
                <i className="fa-solid fa-link text-green-600 mr-2"></i>
                {t('dataExtractor.streaming.urlsFound')} ({streamingData.searchResults.urls.length})
              </h4>
              <div className="max-h-32 overflow-y-auto">
                <ul className="text-sm space-y-1">
                  {streamingData.searchResults.urls.map((url, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-primary-600 font-mono text-xs">#{index + 1}</span>
                      <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-primary-600 hover:underline break-all text-xs"
                      >
                        {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {streamingData.totalUrls > 0 && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-dark-100 mb-2">
                <span>{t('dataExtractor.streaming.processingUrls')}</span>
                <span>{streamingData.processedUrls} / {streamingData.totalUrls}</span>
              </div>
              <div className="w-full bg-light-100 rounded-full h-2">
                <div 
                  className="gradient-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(streamingData.processedUrls / streamingData.totalUrls) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {streamingData.progress.length > 0 && (
            <div className="max-h-40 overflow-y-auto">
              <h4 className="font-semibold text-dark-300 mb-2">{t('dataExtractor.streaming.progressUpdates')}</h4>
              <div className="space-y-2">
                {streamingData.progress.slice(-5).map((item, index) => (
                  <div key={index} className="flex items-start space-x-2 text-sm">
                    <span className={`text-xs ${item.success ? 'text-green-600' : 'text-red-600'}`}>
                      {item.success ? '✅' : '❌'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-dark-100 truncate">
                        {item.url.length > 50 ? `${item.url.substring(0, 50)}...` : item.url}
                      </p>
                      {item.error && (
                        <p className="text-red-600 text-xs">{item.error}</p>
                      )}
                    </div>
                    <span className="text-xs text-dark-100">{item.timestamp}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-4 text-sm mt-4">
            <div className="flex items-center space-x-1">
              <span className="text-green-600">✅</span>
              <span>{streamingData.successfulExtractions} successful</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-red-600">❌</span>
              <span>{streamingData.failedExtractions} failed</span>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {result && <DataResults result={result} schema={extractionSchema} />}

      {/* Schedule Job Modal */}
      <CreateJobModal 
        isOpen={showScheduleModal}
        initialData={{
          mode: mode,
          template_id: selectedTemplate,
          search_query: formData.search_query,
          max_results: formData.max_results,
          urls: mode === 'single' ? [formData.url] : mode === 'batch' ? formData.urls.split('\n').filter(u => u.trim()) : undefined,
          llm_enabled: llmConfig.enabled,
          llm_provider: llmConfig.provider.split('/')[0],
          llm_model: llmConfig.provider.split('/')[1] || llmConfig.provider,
          llm_extraction_prompt: llmConfig.instruction,
          cache_enabled: llmConfig.cache_enabled || false,
          extraction_schema: extractionSchema,
          advanced_options: null
        }}
        onClose={(shouldRefresh) => {
          setShowScheduleModal(false)
          if (shouldRefresh) {
            toast.success('Job scheduled successfully! Go to the Jobs page to manage it.')
          }
        }}
      />

      {/* Excel Import & Mapping Modal */}
      {showExcelModal && excelFile && (
        <ExcelImportModal
          file={excelFile}
          onClose={() => {
            setShowExcelModal(false)
            setExcelFile(null)
            setCrawlUrls(false)
            setCrawlAllUrls(false)
          }}
          onConfirm={(data, mapping, shouldCrawlUrls, shouldCrawlAllUrls) => {
            // Store Excel data
            setExcelData(data)
            setColumnMapping(mapping)
            setCrawlUrls(shouldCrawlUrls)
            setCrawlAllUrls(shouldCrawlAllUrls)
            setShowExcelModal(false)
            
            // Update Extraction Schema with Extract Columns
            const newSchema = {}
            mapping.extractColumns
              .filter(col => col && col !== '' && col !== 'undefined')
              .forEach(col => {
                newSchema[col] = {
                  type: 'string',
                  description: `Extract ${formatFieldName(col)}`,
                  required: false
                }
              })
            
            setExtractionSchema(newSchema)
            
            toast.success(`Excel configured: ${data.length} rows, ${mapping.extractColumns.filter(c => c).length} fields to extract`)
          }}
          extractionSchema={extractionSchema}
        />
      )}
    </div>
  )
}

// Excel Import Modal Component
const ExcelImportModal = ({ file, onClose, onConfirm, extractionSchema }) => {
  const gridRef = useRef(null)
  const [importedData, setImportedData] = useState([])
  const [columns, setColumns] = useState([])
  const [columnMapping, setColumnMapping] = useState({ searchColumns: [], extractColumns: [] })
  const [crawlUrls, setCrawlUrls] = useState(false)
  const [crawlAllUrls, setCrawlAllUrls] = useState(false)

  useEffect(() => {
    if (file) {
      importExcelFile()
    }
  }, [file])

  const importExcelFile = async () => {
    try {
      const reader = new FileReader()
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          
          // Get first sheet
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          
          // Convert to JSON with first row as header
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' })
          
          if (jsonData.length === 0) {
            toast.error('Excel file is empty')
            return
          }
          
          // First row is headers
          const headers = jsonData[0]
          const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''))
          
          // Create columns
          const cols = headers.map(header => ({
            prop: String(header),
            name: String(header),
            size: 150,
            sortable: true
          }))
          
          // Create data rows
          const rows = dataRows.map(row => {
            const rowObj = {}
            headers.forEach((header, idx) => {
              rowObj[String(header)] = row[idx] !== undefined ? String(row[idx]) : ''
            })
            return rowObj
          })
          
          setColumns(cols)
          setImportedData(rows)
          
          // Auto-detect filled vs empty columns
          analyzeColumns(rows, cols)
          
          toast.success(`Excel file imported: ${rows.length} rows, ${cols.length} columns`)
        } catch (error) {
          console.error('Excel parsing failed:', error)
          toast.error('Failed to parse Excel file: ' + error.message)
        }
      }
      
      reader.onerror = () => {
        toast.error('Failed to read file')
      }
      
      reader.readAsArrayBuffer(file)
    } catch (error) {
      console.error('Excel import failed:', error)
      toast.error('Failed to import Excel file')
    }
  }

  const analyzeColumns = (data, cols) => {
    if (!data || data.length === 0) return

    const searchCols = []
    const extractCols = []

    cols.forEach(col => {
      // Skip columns with empty/invalid names
      if (!col.prop || col.prop === '' || col.prop === 'undefined') {
        return
      }

      // Check if column has data in any row
      const hasData = data.some(row => {
        const value = row[col.prop]
        return value && value !== '' && value !== '-' && value !== null && value !== undefined
      })

      if (hasData) {
        // Get example value from first row
        const exampleValue = data[0]?.[col.prop] || ''
        searchCols.push({
          name: col.prop,
          example: exampleValue,
          isUrl: isUrlColumn(exampleValue)
        })
      } else {
        extractCols.push(col.prop)
      }
    })

    setColumnMapping({
      searchColumns: searchCols,
      extractColumns: extractCols
    })
  }

  const isUrlColumn = (value) => {
    if (!value || typeof value !== 'string') return false
    const urlPattern = /^(https?:\/\/|www\.)/i
    return urlPattern.test(value.trim())
  }

  const handleConfirmImport = () => {
    // Pass data, mapping, crawlUrls, and crawlAllUrls options to parent
    onConfirm(importedData, columnMapping, crawlUrls, crawlAllUrls)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-7xl w-full h-[95vh] overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-light-200/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-dark-300">
                <i className="fa-solid fa-file-excel text-green-600 mr-2"></i>
                Import Excel File
              </h3>
              <p className="text-sm text-dark-100 mt-1">
                {file.name} • Review and configure column mapping
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-dark-100 hover:text-dark-300 transition-colors"
            >
              <i className="fa-solid fa-xmark text-3xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Excel Preview */}
          <div className="mb-6">
            <h4 className="text-lg font-bold text-dark-300 mb-4">
              <i className="fa-solid fa-table text-primary-500 mr-2"></i>
              Excel Data Preview
            </h4>
            <div style={{ height: '300px' }}>
              {columns.length > 0 ? (
                <RevoGrid
                  ref={gridRef}
                  columns={columns}
                  source={importedData}
                  theme="compact"
                  resize={true}
                  readonly={true}
                  plugins={[ExportExcelPlugin]}
                  additionalData={{ excel: { allowDrag: true } }}
                />
              ) : (
                <div className="flex items-center justify-center h-full bg-light-100 rounded-lg border-2 border-dashed border-light-200">
                  <div className="text-center">
                    <i className="fa-solid fa-spinner fa-spin text-4xl text-primary-500 mb-3"></i>
                    <p className="text-sm text-dark-100">Importing Excel file...</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Column Mapping */}
          {columns.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-bold text-dark-300 mb-4">
                <i className="fa-solid fa-diagram-project text-primary-500 mr-2"></i>
                Column Mapping
              </h4>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Search Columns */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="font-bold text-blue-800 mb-3 flex items-center">
                    <i className="fa-solid fa-magnifying-glass mr-2"></i>
                    Search Columns ({columnMapping.searchColumns.length})
                  </h5>
                  <p className="text-xs text-blue-700 mb-3">
                    These columns contain data and will be used as search terms
                  </p>
                  
                  {/* URL Crawling Option */}
                  {columnMapping.searchColumns.some(col => col.isUrl) && (
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={crawlUrls}
                          onChange={(e) => setCrawlUrls(e.target.checked)}
                          className="mt-0.5 mr-2 h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="text-xs font-semibold text-yellow-800">
                            <i className="fa-solid fa-link mr-1"></i>
                            URL detected - Also crawl these websites?
                          </span>
                          <p className="text-xs text-yellow-700 mt-1">
                            If checked, URLs in search columns will be directly crawled in addition to searching
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                  
                  {/* Crawl All URLs Option */}
                  <div className="mb-3 p-3 bg-purple-50 border border-purple-300 rounded-lg">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={crawlAllUrls}
                        onChange={(e) => setCrawlAllUrls(e.target.checked)}
                        className="mt-0.5 mr-2 h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <div>
                        <span className="text-xs font-semibold text-purple-800">
                          <i className="fa-solid fa-layer-group mr-1"></i>
                          Deep Data Enrichment - Crawl all 15 URLs per row
                        </span>
                        <p className="text-xs text-purple-700 mt-1">
                          If checked: Crawls all 15 URLs and uses the most complete data (slower, better quality)
                        </p>
                        <p className="text-xs text-purple-700">
                          If unchecked: Stops at first successful extraction (faster, good enough)
                        </p>
                      </div>
                    </label>
                  </div>
                  
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {columnMapping.searchColumns.map(col => (
                      <div key={col.name} className="px-3 py-2 bg-white rounded border border-blue-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-dark-300 flex items-center">
                              <i className={`fa-solid ${col.isUrl ? 'fa-link' : 'fa-database'} mr-2 text-blue-600`}></i>
                              {formatFieldName(col.name)}
                            </div>
                            {col.example && (
                              <div className="text-xs text-dark-100 mt-1 italic truncate">
                                Example: {col.example}
                              </div>
                            )}
                          </div>
                          {col.isUrl && (
                            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                              URL
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Extract Columns */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h5 className="font-bold text-green-800 mb-3 flex items-center">
                    <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
                    Extract Columns ({columnMapping.extractColumns.filter(c => c && c !== '').length})
                  </h5>
                  <p className="text-xs text-green-700 mb-3">
                    These columns are empty and will be filled by crawling
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {columnMapping.extractColumns
                      .filter(col => col && col !== '' && col !== 'undefined')
                      .map(col => (
                        <div key={col} className="px-3 py-2 bg-white rounded border border-green-200 text-sm font-medium text-dark-300">
                          <i className="fa-solid fa-circle-notch mr-2 text-green-600"></i>
                          {formatFieldName(col)}
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info & Stats */}
          {importedData.length > 0 && (
            <div className="mb-6 p-4 bg-gradient-to-r from-primary-dark to-primary-500 rounded-xl text-white">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="text-xs opacity-80">Total Rows</div>
                  <div className="text-2xl font-bold">{importedData.length}</div>
                </div>
                <div>
                  <div className="text-xs opacity-80">Search Columns</div>
                  <div className="text-2xl font-bold">{columnMapping.searchColumns.length}</div>
                </div>
                <div>
                  <div className="text-xs opacity-80">Extract Columns</div>
                  <div className="text-2xl font-bold">{columnMapping.extractColumns.filter(c => c && c !== '').length}</div>
                </div>
                <div>
                  <div className="text-xs opacity-80">Estimated Time</div>
                  <div className="text-2xl font-bold">~{Math.ceil(importedData.length * 0.5)}min</div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-white border-2 border-light-200/30 hover:border-red-500 text-dark-300 hover:text-red-600 rounded-lg font-semibold transition-all"
            >
              <i className="fa-solid fa-xmark mr-2"></i>
              Cancel
            </button>
            <button
              onClick={handleConfirmImport}
              disabled={importedData.length === 0}
              className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fa-solid fa-check mr-2"></i>
              Confirm & Apply Schema ({importedData.length} rows)
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DataExtraction
